'use client'
import { useState, useEffect, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useNotificationContext } from '@/context/useNotificationContext'
import { useAuthStore } from '@/store/authStore'
import api from '@/utils/axiosInstance'
import useQueryParams from '@/hooks/useQueryParams'
import { getBiometricKind, saveBiometricCredentials } from '@/utils/nativeBiometric'
import { resolveUnifiedPostLoginPath } from '@/utils/loginAppTarget'
import { usePosStoreContext } from '@/store/posStoreContext'

const schema = yup.object({
  identifier: yup.string().required('Username or email is required'),
  pin: yup
    .string()
    .matches(/^\d{4}$/, 'PIN must be exactly 4 digits')
    .required('PIN is required'),
})

type FormData = yup.InferType<typeof schema>

export const useSignIn = () => {
  const [loading, setLoading] = useState(false)
  const router = useRouter()
  const { showNotification } = useNotificationContext()
  const setAuth = useAuthStore((state) => state.setAuth)
  const setSettings = useAuthStore((state) => state.setSettings)
  const clearPosStore = usePosStoreContext((s) => s.clearStore)
  const queryParams = useQueryParams()
  const form = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      identifier: '',
      pin: '',
    },
  })

  const { setValue } = form

  useEffect(() => {
    const savedIdentifier = localStorage.getItem('last_login_identifier')
    if (savedIdentifier) {
      setValue('identifier', savedIdentifier)
    }
  }, [setValue])

  const performLogin = useCallback(
    async (identifier: string, pin: string) => {
      setLoading(true)
      try {
        const res = await api.post('/api/auth/login', { identifier, pin })
        const { token, user } = res.data

        if (token && user) {
          localStorage.setItem('last_login_identifier', identifier)
          setAuth(token, user)
          if (user?.role !== 'superadmin') {
            const response = await api.get('/api/personal-settings')
            setSettings(response.data)
          }

          const biometricKind = await getBiometricKind()
          if (biometricKind) {
            try {
              await saveBiometricCredentials(identifier, pin)
            } catch {
              // optional
            }
          }

          const landing = resolveUnifiedPostLoginPath(user)
          if (landing.landingRole === 'unknown') {
            showNotification({
              message: 'This account has no app access. Ask your admin to set permissions.',
              variant: 'danger',
            })
            setLoading(false)
            return
          }

          // Keep last POS store if still assigned; layout will default-select otherwise.
          if (!landing.path.startsWith('/pos')) {
            clearPosStore()
          }

          showNotification({
            message: `Login successful — ${landing.label}`,
            variant: 'success',
          })
          router.push(queryParams['redirectTo'] ?? landing.path)
        } else {
          showNotification({ message: 'Invalid login response', variant: 'danger' })
        }
      } catch (error: unknown) {
        const message =
          error &&
          typeof error === 'object' &&
          'response' in error &&
          error.response &&
          typeof error.response === 'object' &&
          'data' in error.response &&
          error.response.data &&
          typeof error.response.data === 'object' &&
          'message' in error.response.data &&
          typeof error.response.data.message === 'string'
            ? error.response.data.message
            : 'Login failed'
        showNotification({ message, variant: 'danger' })
      } finally {
        setLoading(false)
      }
    },
    [clearPosStore, queryParams, router, setAuth, setSettings, showNotification],
  )

  const login = form.handleSubmit(async ({ identifier, pin }) => {
    await performLogin(identifier, pin)
  })

  const loginWithCredentials = useCallback(
    async (identifier: string, pin: string) => {
      setValue('identifier', identifier)
      setValue('pin', pin)
      await performLogin(identifier, pin)
    },
    [performLogin, setValue],
  )

  return { form, login, loginWithCredentials, loading }
}
